/**
 * Feature toggle hooks for addon features.
 *
 * These hooks allow components to check if features are enabled
 * for the current study and conditionally render UI elements.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useNavigationStore } from '@/stores/navigationStore'

// =============================================================================
// Types
// =============================================================================

export interface FeatureDefinition {
  key: string
  name: string
  description: string
  category: 'addon' | 'core'
}

export interface StudyFeaturesResponse {
  studyId: string
  enabledFeatures: Record<string, boolean>
  availableFeatures: FeatureDefinition[]
}

// =============================================================================
// API Functions
// =============================================================================

async function fetchStudyFeatures(studyId: string): Promise<StudyFeaturesResponse> {
  const response = await api.get(`/studies/${studyId}/features`)
  return response.data
}

async function updateStudyFeatures(
  studyId: string,
  features: Record<string, boolean>
): Promise<StudyFeaturesResponse> {
  const response = await api.put(`/studies/${studyId}/features`, { features })
  return response.data
}

async function toggleFeature(
  studyId: string,
  featureKey: string,
  enabled: boolean
): Promise<StudyFeaturesResponse> {
  const response = await api.patch(`/studies/${studyId}/features/${featureKey}`, {
    feature_key: featureKey,
    enabled,
  })
  return response.data
}

async function fetchAvailableFeatures(): Promise<FeatureDefinition[]> {
  const response = await api.get('/studies/features/available')
  return response.data
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to get all feature settings for a study.
 *
 * @param studyId - The study ID to get features for
 * @returns Query result with study features
 */
export function useStudyFeatures(studyId: string | undefined) {
  return useQuery({
    queryKey: ['study-features', studyId],
    queryFn: () => fetchStudyFeatures(studyId!),
    enabled: !!studyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to check if a specific feature is enabled for a study.
 *
 * This is the main hook to use when conditionally rendering feature UI.
 *
 * @param studyId - The study ID to check
 * @param featureKey - The feature key to check (e.g., 'issues_tracker')
 * @returns Boolean indicating if the feature is enabled
 *
 * @example
 * ```tsx
 * const issuesEnabled = useFeatureEnabled(studyId, 'issues_tracker')
 *
 * if (!issuesEnabled) {
 *   return null // Don't render issues UI
 * }
 *
 * return <IssuesPanel />
 * ```
 */
export function useFeatureEnabled(
  studyId: string | undefined,
  featureKey: string
): boolean {
  const { data } = useStudyFeatures(studyId)

  if (!data || !data.enabledFeatures) {
    return false
  }

  return Boolean(data.enabledFeatures[featureKey])
}

/**
 * Hook to check multiple features at once.
 *
 * @param studyId - The study ID to check
 * @param featureKeys - Array of feature keys to check
 * @returns Object with feature keys mapped to their enabled status
 *
 * @example
 * ```tsx
 * const features = useFeaturesEnabled(studyId, ['issues_tracker', 'data_compare'])
 *
 * return (
 *   <>
 *     {features.issues_tracker && <IssuesPanel />}
 *     {features.data_compare && <DataComparePanel />}
 *   </>
 * )
 * ```
 */
export function useFeaturesEnabled(
  studyId: string | undefined,
  featureKeys: string[]
): Record<string, boolean> {
  const { data } = useStudyFeatures(studyId)

  const result: Record<string, boolean> = {}
  for (const key of featureKeys) {
    result[key] = data?.enabledFeatures?.[key] ?? false
  }

  return result
}

/**
 * Hook to get the list of all available features.
 *
 * @returns Query result with available features
 */
export function useAvailableFeatures() {
  return useQuery({
    queryKey: ['available-features'],
    queryFn: fetchAvailableFeatures,
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
  })
}

/**
 * Hook to update feature settings for a study.
 *
 * @returns Mutation for updating features
 */
export function useUpdateStudyFeatures(studyId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (features: Record<string, boolean>) => {
      if (!studyId) throw new Error('Study ID is required')
      return updateStudyFeatures(studyId, features)
    },
    onSuccess: (data) => {
      // Update cache with new data
      queryClient.setQueryData(['study-features', studyId], data)
      // Also invalidate the study query to update enabled_features there
      queryClient.invalidateQueries({ queryKey: ['study', studyId] })
      // Invalidate navigation so sidebar picks up newly created/hidden addon pages
      queryClient.invalidateQueries({ queryKey: ['study-navigation-tree', studyId] })
      // Clear Zustand cache so sidebar reloads from server
      useNavigationStore.getState().clearStudyNavigation()
    },
  })
}

/**
 * Hook to toggle a single feature for a study.
 *
 * @returns Mutation for toggling a feature
 */
export function useToggleFeature(studyId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ featureKey, enabled }: { featureKey: string; enabled: boolean }) => {
      if (!studyId) throw new Error('Study ID is required')
      return toggleFeature(studyId, featureKey, enabled)
    },
    onSuccess: (data) => {
      // Update cache with new data from response - this immediately updates the UI
      queryClient.setQueryData(['study-features', studyId], data)
      // Invalidate the study query to refresh study.enabled_features elsewhere
      queryClient.invalidateQueries({ queryKey: ['study', studyId] })
      // Invalidate navigation so sidebar picks up newly created/hidden addon pages
      queryClient.invalidateQueries({ queryKey: ['study-navigation-tree', studyId] })
      // Clear Zustand cache so sidebar reloads from server
      useNavigationStore.getState().clearStudyNavigation()
    },
  })
}

// =============================================================================
// Feature Keys Constants
// =============================================================================

/**
 * Feature keys for type safety and autocomplete.
 */
export const FEATURE_KEYS = {
  ISSUES_TRACKER: 'issues_tracker',
  DATA_COMPARE: 'data_compare',
  PATIENT_PROFILE: 'patient_profile',
  AI_SUITE: 'ai_suite',
  NARRATIVE: 'narrative',
} as const

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS]
